import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import DynamicSVG from "aldoo-ra/DynamicSVG"
import CMSRender from "aldoo-ra/CMS/cms-render"
import Reactions from "aldoo-ra/Reactions"
import FingerprintJS from "@fingerprintjs/fingerprintjs"
import PlatformAuth from "aldoo-ra/PlatformAuth"

// Get the singleton instance
const reactionsManager = Reactions.getInstance()

/**
 * ArticleFeedback component to collect user feedback on articles
 * @param {Object} props
 * @param {Object} props.article - The article object
 * @param {Function} props.onFeedback - Callback function when feedback is provided
 */
const ArticleFeedback = ({ article, onFeedback }) => {
  // Local state for feedback
  const [feedbackGiven, setFeedbackGiven] = useState(false)
  const [feedbackType, setFeedbackType] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  // Start with hidden state by default
  const [shouldShow, setShouldShow] = useState(false)
  // Track if this is a fresh reaction in the current session
  const [isNewReaction, setIsNewReaction] = useState(false)
  const [auth] = PlatformAuth.useAuth()

  // When component mounts, check if the user has already reacted
  useEffect(() => {
    if (!article?._id) {
      setIsLoading(false)
      return
    }

    // Reset all state when article changes
    setFeedbackGiven(false)
    setFeedbackType(null)
    setIsNewReaction(false)
    setShouldShow(false)
    setError(null)

    checkExistingReaction()
  }, [article?._id])

  // Function to get a unique identifier for the user
  const getIdentifier = async () => {
    if (auth?._id) {
      return auth._id
    }

    // Use fingerprint for anonymous users
    if (!window.fpPromise) {
      window.fpPromise = FingerprintJS.load()
    }
    const fp = await window.fpPromise
    const result = await fp.get()
    return result.visitorId
  }

  // Check if the user has already reacted to this article
  const checkExistingReaction = async () => {
    try {
      setIsLoading(true)
      const identifier = await getIdentifier()

      // Get status for both possible reactions
      const result = await reactionsManager.getStatus({
        contentId: article._id,
        contentType: 'HCArticle',
        identifier,
        isAnonymous: !auth,
      })

      if (result.error) {
        console.warn("Error checking reaction status:", result.error)
        setIsLoading(false)
        return
      }

      // Check if user has already reacted
      let hasReacted = false

      if (result.reaction && Array.isArray(result.reaction) && result.reaction.length > 0) {
        if (result.reaction.includes("positive")) {
          setFeedbackType("positive")
          hasReacted = true
        } else if (result.reaction.includes("negative")) {
          setFeedbackType("negative")
          hasReacted = true
        }
      }

      // Set feedback state if user has already reacted
      if (hasReacted) {
        setFeedbackGiven(true)
      }

      // Only show the component if:
      // 1. The user has not reacted yet, OR
      // 2. This is a new reaction from the current session
      if (!hasReacted || isNewReaction) {
        setShouldShow(true)
      }

      setIsLoading(false)
    } catch (err) {
      console.error("Error checking existing reactions:", err)
      setIsLoading(false)
    }
  }

  const handleFeedback = async (type) => {
    // Don't allow multiple submissions
    if (feedbackGiven || isSubmitting) return

    // Update local state immediately for better UX
    setFeedbackType(type)
    setFeedbackGiven(true)
    setIsSubmitting(true)
    setError(null)
    // Flag that this is a new reaction in this session
    setIsNewReaction(true)

    try {
      const identifier = await getIdentifier()

      const response = await reactionsManager.toggleReaction({
        contentId: article._id,
        contentType: 'HCArticle',
        identifier,
        isAnonymous: !auth,
        reaction: type, // "positive" or "negative"
      })

      if (response.error) {
        throw new Error(response.error)
      }

      // Call the callback if provided
      if (onFeedback) {
        onFeedback(type)
      }
    } catch (err) {
      console.error("Error submitting feedback:", err)
      setError("Error saving your feedback. Please try again later.")

      // Revert UI state on error
      setFeedbackType(null)
      setFeedbackGiven(false)
      setIsNewReaction(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Render the question state ("Did you find this useful?")
   */
  const renderQuestionState = (data) => (
    <div className="w-full flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="w-full h-full flex flex-col items-center md:items-start justify-center">
        <Typography variant="h3" className="text-text justify-center md:justify-start mt-0 mb-4">
          {data.question_title || "Did you find this useful?"} 
        </Typography>

        <Typography variant="body1_dim" className="justify-center md:justify-start my-0">
          {data.question_subtitle || "Your feedback helps us improve."}
        </Typography>
      </div>

      {/* Rate Buttons */}
      <div className="flex gap-4">
        <button
          onClick={() => handleFeedback("positive")}
          disabled={isSubmitting || isLoading}
          className="flex items-center justify-center w-[72px] h-[72px] rounded-[20px] bg-paper hover:border-primary shadow-soft_lg transition-colors"
          aria-label="Yes, this was helpful"
        >
          <DynamicSVG
            url={data.icon_rate_up}
            className="w-[50px] h-[50px] text-text"
          />
        </button>

        <button
          onClick={() => handleFeedback("negative")}
          disabled={isSubmitting || isLoading}
          className="flex items-center justify-center w-[72px] h-[72px] rounded-[20px] bg-paper hover:border-primary shadow-soft_lg transition-colors"
          aria-label="No, this was not helpful"
        >
          <DynamicSVG
            url={data.icon_rate_down}
            className="w-[50px] h-[50px] text-text"
          />
        </button>
      </div>
    </div>
  )

  /**
   * Render the thank you state
   */
  const renderThankYouState = (data) => (
    <div className="w-full flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="w-full h-full flex flex-col items-center md:items-start justify-center">
        <Typography variant="h3" className="text-text justify-center md:justify-start my-0">
          {data.thanks_title || "Thank you!"}
        </Typography>
      </div>

      {/* Rate Buttons */}
      <div className="flex gap-4">
        <button
          disabled
          className={`flex items-center justify-center w-[72px] h-[72px] rounded-[20px] shadow-soft_lg ${
            feedbackType === "positive"
              ? "bg-primary text-white"
              : "bg-paper"
          }`}
          aria-label="Thumbs up"
        >
          <DynamicSVG
            url={data.icon_rate_up}
            className={`w-[50px] h-[50px] text-text  ${
              feedbackType === "positive" ? "text-white" : "text-text"
            }`}
          />
        </button>

        <button
          disabled
          className={`flex items-center justify-center w-[72px] h-[72px] rounded-[20px] shadow-soft_lg ${
            feedbackType === "negative"
              ? "bg-primary text-white"
              : "bg-paper"
          }`}
          aria-label="Thumbs down"
        >
          <DynamicSVG
            url={data.icon_rate_down}
            className={`w-[50px] h-[50px] text-text  ${
              feedbackType === "negative" ? "text-white" : "text-text"
            }`}
          />
        </button>
      </div>
    </div>
  )

  // If the component shouldn't be shown (user already reacted) or is still loading, return null
  if (!shouldShow || isLoading) {
    return null
  }

  return (
    <CMSRender
      contentType="ArticleFeedback"
      match={{ "content.id": "hc_feedback" }}
      renderItem={(data) => {
        // If no article or missing data, don't render
        if (!article || !article._id) {
          return null
        }

        return (
          <div className="w-[343px] md:w-[680px] lg:w-[590px] xl:w-[730px] md:h-[123px] xl:h-[160px] bg-gradient_showcase_card py-8 pl-10 md:pl-8 pr-10 rounded-[30px] mt-14 mx-auto lg:mx-0">
            <div className="flex items-center justify-between w-full h-full">
              {!feedbackGiven
                ? renderQuestionState(data)
                : renderThankYouState(data)}
            </div>

            {error && <Typography variant="error">{error}</Typography>}
          </div>
        )
      }}
    />
  )
}

export default ArticleFeedback
